/**
 * 도메인 컨트롤러 컴포지션 루트 — 순수 팩토리 (일감 G2 / architecture-review §2 패러다임 규칙 6).
 *
 * GameScene.create()의 컨트롤러 생성 블록을 Phaser-free로 분리:
 *  - 신규 컨트롤러 추가는 GameScene(멀티세션 공유 허브) 대신 이 파일에서 일어난다.
 *  - 외부 의존(저장/설정 어댑터·분석 sink·난수·시각)은 옵션 주입 — vitest에서 그대로 생성 가능.
 *
 * 여기서 만든 인스턴스는 "새 게임" 초기 상태다. 저장이 있으면 GameScene.applySaveData가
 * SaveCodec.decode 산출물로 인스턴스를 통째 교체한다(getter 주입 stale 회피 패턴과 한 쌍).
 */
import {
  INITIAL_GOLD,
  INITIAL_DIAMOND,
  REBIRTH_UPGRADE_DEFS,
  MODULE_BRICK_BALANCE,
  MYSTERY_BRICK,
  SOUND_MASTER_GAIN,
} from "../data/balance.js";
import type { SaveAdapter } from "@yjsgames/save";
import {
  type AnalyticsSink,
  NoopAnalyticsSink,
} from "@yjsgames/analytics-ports";
import { AnalyticsMilestoneController } from "../systems/AnalyticsMilestoneController.js";
import { ChapterController } from "../systems/ChapterController.js";
import { EconomyController } from "../systems/EconomyController.js";
import { ProjectileSpecBuilder } from "../systems/ProjectileSpecBuilder.js";
import { ModuleInventory } from "../systems/ModuleInventory.js";
import { PlayerModuleLevelController } from "../systems/PlayerModuleLevelController.js";
import { ModuleMasteryController } from "../systems/ModuleMasteryController.js";
import { SynergyLevelController } from "../systems/SynergyLevelController.js";
import { ModuleSynergyController } from "../systems/ModuleSynergyController.js";
import { ModuleGridController } from "../systems/ModuleGridController.js";
import { SpaceshipController } from "../systems/SpaceshipController.js";
import type { GoldModuleBox } from "../systems/GoldModuleBox.js";
import { DiamondUpgradeBox } from "../systems/DiamondUpgradeBox.js";
import { RebirthController } from "../systems/RebirthController.js";
import { StatUpgradeController } from "../systems/StatUpgradeController.js";
import { RelicController } from "../systems/RelicController.js";
import { DungeonController } from "../systems/DungeonController.js";
import { DailyShopController } from "../systems/DailyShopController.js";
import { ModuleFusionController } from "../systems/ModuleFusionController.js";
import { MysteryBlockController } from "../systems/MysteryBlockController.js";
import { ModuleBrickSaveSystem } from "../systems/ModuleBrickSaveSystem.js";
import { BrickSettingsStore } from "../systems/BrickSettingsStore.js";
import { SoundController } from "../systems/SoundController.js";
import { createGoldBox } from "../systems/SaveCodec.js";
import { ModuleRegistry } from "../modules/ModuleRegistry.js";
import {
  createCounterIdSource,
  type InstanceIdSource,
} from "../models/ModuleInstance.js";
import { UpgradeInventory } from "../models/UpgradeInventory.js";
import { GuideQuestController } from "@yjsgames/guide-quest";
import { GUIDE_QUEST_DEFINITIONS } from "../data/guideQuestData.js";
import { defaultSpaceship, moduleGridStateForSpaceship } from "../data/spaceshipData.js";

export interface CreateControllersOptions {
  /** 진행 저장 어댑터 — 호출자(main.ts→registry)가 결정. 미주입 시 ModuleBrickSaveSystem 기본값(LocalStorage). */
  saveAdapter?: SaveAdapter;
  /** 설정(로컬 전용, 클라우드 미포함) 어댑터. 미주입 시 LocalStorage 기본값. */
  settingsAdapter?: SaveAdapter;
  /** 분석 sink — 미주입 시 Noop. */
  analyticsSink?: AnalyticsSink;
  /** 난수원 (일일상점 추첨) — 기본 Math.random. */
  random?: () => number;
  /** 인스턴스 id prefix 시각원 — 기본 Date.now. */
  now?: () => number;
}

export interface DomainControllers {
  chapter: ChapterController;
  economy: EconomyController;
  specBuilder: ProjectileSpecBuilder;
  moduleRegistry: ModuleRegistry;
  inventory: ModuleInventory;
  /** 모듈 레벨 — moduleId별 플레이어 영구 자산(환생 유지). 인스턴스 등급과 직교(2축). */
  playerModuleLevels: PlayerModuleLevelController;
  /** 모듈 숙련도 — moduleId별 회차 XP 기반 노드 해금(레벨·등급과 직교, 환생 시 초기화). */
  moduleMastery: ModuleMasteryController;
  /** 시너지 도감 발견 — 활성 경로 모듈 조합으로 발동한 시너지 id 집합(최초 발견 보상 1회 dedup, 환생 유지). */
  moduleSynergy: ModuleSynergyController;
  /** 시너지 강화 레벨 — synergyId별 영구 효과 레벨(강화석 성장, 환생 유지). */
  synergyLevels: SynergyLevelController;
  spaceship: SpaceshipController;
  grid: ModuleGridController;
  upgradeInv: UpgradeInventory;
  idSource: InstanceIdSource;
  goldBox: GoldModuleBox;
  diamondBox: DiamondUpgradeBox;
  rebirth: RebirthController;
  statUpgrade: StatUpgradeController;
  rebirthUpgrade: StatUpgradeController;
  relic: RelicController;
  dungeon: DungeonController;
  dailyShop: DailyShopController;
  fusion: ModuleFusionController;
  mysteryBlock: MysteryBlockController;
  /** 데이터 주도 가이드퀘스트 진행 컨트롤러 — 정의는 guideQuests.tsv, 상태는 SaveData.guideQuest. */
  guideQuest: GuideQuestController;
  saveSystem: ModuleBrickSaveSystem;
  settingsStore: BrickSettingsStore;
  analytics: AnalyticsSink;
  /** 분석 마일스톤 1회성 추적 — 계정 생애 1회 퍼널 이벤트 dedup (first_fire/first_purchase/rebirth_ready). */
  analyticsMilestones: AnalyticsMilestoneController;
  soundController: SoundController;
}

export function createControllers(
  opts: CreateControllersOptions = {},
): DomainControllers {
  const random = opts.random ?? Math.random;
  const now = opts.now ?? Date.now;
  return {
    chapter: new ChapterController(1, 1),
    // 새 게임 초기 재화 — global.tsv INITIAL_GOLD/INITIAL_DIAMOND (저장 있으면 applySaveData가 교체).
    economy: new EconomyController(INITIAL_GOLD, INITIAL_DIAMOND),
    specBuilder: new ProjectileSpecBuilder(),
    moduleRegistry: new ModuleRegistry(),
    inventory: new ModuleInventory(),
    playerModuleLevels: new PlayerModuleLevelController(),
    moduleMastery: new ModuleMasteryController(),
    moduleSynergy: new ModuleSynergyController(),
    synergyLevels: new SynergyLevelController(),
    spaceship: new SpaceshipController(),
    grid: new ModuleGridController(moduleGridStateForSpaceship(defaultSpaceship())),
    upgradeInv: new UpgradeInventory(),
    idSource: createCounterIdSource(`m${now()}-`),
    goldBox: createGoldBox(),
    diamondBox: new DiamondUpgradeBox(),
    rebirth: new RebirthController(),
    statUpgrade: new StatUpgradeController(),
    rebirthUpgrade: new StatUpgradeController(undefined, REBIRTH_UPGRADE_DEFS),
    relic: new RelicController(),
    dungeon: new DungeonController(),
    dailyShop: new DailyShopController(random),
    // 새 게임 융합 카운터 0 — 저장 있으면 applySaveData가 decode 산출물로 교체 (SaveCodec과 동형 생성).
    fusion: new ModuleFusionController(undefined, MODULE_BRICK_BALANCE.fusionDiamondCost),
    mysteryBlock: new MysteryBlockController(
      undefined,
      MYSTERY_BRICK.diamondDailyLimit,
      MYSTERY_BRICK.relicDailyLimit,
    ),
    guideQuest: new GuideQuestController(GUIDE_QUEST_DEFINITIONS),
    saveSystem: new ModuleBrickSaveSystem(opts.saveAdapter),
    settingsStore: new BrickSettingsStore(opts.settingsAdapter),
    analytics: opts.analyticsSink ?? new NoopAnalyticsSink(),
    // 새 게임 빈 마일스톤 — 저장 있으면 applySaveData가 SaveCodec decode 산출물로 교체.
    analyticsMilestones: new AnalyticsMilestoneController(),
    soundController: new SoundController(undefined, SOUND_MASTER_GAIN),
  };
}
